/**
 * Bero brand primitives — View-built (no react-native-svg: keeps the bundle
 * OTA-able). Geometry ports design/source/screens-onboard.jsx BeroMark:
 * forest-green gate arch + amber figure (head circle + body pill).
 */
import React, { useEffect } from 'react';
import { Animated, Easing, View, useWindowDimensions } from 'react-native';

import { useTheme } from '@/theme';
import { BeroMark, BrandLockup, Wordmark } from '@/ui/brand';

export { BeroMark, BrandLockup, Wordmark };




/**
 * O1 "Gate draws" splash. Arch rises + fades (approximation of the stroke
 * draw — no SVG), figure fades up at ~0.9s, wordmark at ~1.3s, onDone ~2.0s.
 * Kept to 2s total — anything longer makes the app FEEL slow (user, 2026-07-18).
 */
export function GateSplash({ onDone }: { onDone: () => void }) {
  const { colors } = useTheme();
  const { height: winH } = useWindowDimensions();
  const [arch] = React.useState(() => new Animated.Value(0));
  const [fig] = React.useState(() => new Animated.Value(0));
  const [word] = React.useState(() => new Animated.Value(0));

  useEffect(() => {
    Animated.sequence([
      Animated.timing(arch, { toValue: 1, duration: 1000, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
    ]).start();
    Animated.timing(fig, { toValue: 1, duration: 550, delay: 900, easing: Easing.out(Easing.ease), useNativeDriver: true }).start();
    Animated.timing(word, { toValue: 1, duration: 500, delay: 1300, easing: Easing.out(Easing.ease), useNativeDriver: true }).start();
    const t = setTimeout(onDone, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const H = 100;
  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0, alignItems: 'center', justifyContent: 'center', gap: 22, paddingBottom: winH * 0.06 }}>
      <View style={{ width: 86, height: H }}>
        <Animated.View style={{ position: 'absolute', inset: 0, opacity: arch, transform: [{ translateY: arch.interpolate({ inputRange: [0, 1], outputRange: [14, 0] }) }] }}>
          {/* draw the arch with the splash's open-bottom geometry (viewBox 50x58 → 86x100) */}
          <View
            style={{
              position: 'absolute', left: 86 * (7 / 50) - 8, top: 100 * (6 / 58), width: 86 * (36 / 50) + 16, height: 100 * (50 / 58),
              borderWidth: 15, borderBottomWidth: 0, borderColor: colors.primary500,
              borderTopLeftRadius: 43, borderTopRightRadius: 43,
            }}
          />
        </Animated.View>
        <Animated.View style={{ position: 'absolute', inset: 0, opacity: fig, transform: [{ translateY: fig.interpolate({ inputRange: [0, 1], outputRange: [6, 0] }) }] }}>
          <View style={{ position: 'absolute', left: 43 - 10.5, top: 100 * (24 / 58), width: 21, height: 21, borderRadius: 10.5, backgroundColor: colors.accent500 }} />
          <View style={{ position: 'absolute', left: 43 - 8.5, top: 100 * (40 / 58), width: 17, height: 100 * (16 / 58), borderRadius: 8.5, backgroundColor: colors.accent500 }} />
        </Animated.View>
      </View>
      <Animated.View style={{ opacity: word, transform: [{ translateY: word.interpolate({ inputRange: [0, 1], outputRange: [10, 0] }) }] }}>
        <Wordmark size={34} />
      </Animated.View>
    </View>
  );
}

/** Skyline of arches for the login hero ("Gates of the city", L5).
 * Pass width to fill a pane (desktop split view); bare = plain arches, no
 * band background/border. */
export function ArchSkyline({ height = 230, width, bare = false }: { height?: number; width?: number; bare?: boolean }) {
  const { colors } = useTheme();
  const { width: winW } = useWindowDimensions();
  const W = width ?? Math.min(winW, 560);
  const u = W / 310; // design viewBox width 310
  const arch = (x: number, w: number, h: number, color: string, key: string) => (
    <View key={key}
      style={{
        position: 'absolute', bottom: 0, left: x * u, width: w * u, height: h * u,
        backgroundColor: color, borderTopLeftRadius: (w / 2) * u, borderTopRightRadius: (w / 2) * u,
      }}
    />
  );
  return (
    <View style={{
      height,
      width: width ?? '100%',
      backgroundColor: bare ? 'transparent' : colors.canvas1,
      borderBottomWidth: bare ? 0 : 0.5,
      borderBottomColor: colors.borderSoft,
      overflow: 'hidden',
    }}>
      <View style={{ position: 'absolute', bottom: 0, left: '50%', marginLeft: -(W / 2), width: W, height }}>
        {arch(-10, 68, 122, colors.canvas2, 'a')}
        {arch(250, 76, 136, colors.canvas2, 'b')}
        {arch(62, 52, 94, colors.canvas2, 'c')}
        {arch(196, 60, 110, colors.primary100, 'd')}
        {arch(100, 56, 100, colors.primary100, 'e')}
        {/* center gate: outlined arch + amber figure */}
        <View style={{
          position: 'absolute', bottom: 0, left: 128 * u, width: 54 * u, height: 110 * u,
          borderWidth: 12 * u, borderBottomWidth: 0, borderColor: colors.primary500,
          borderTopLeftRadius: 27 * u, borderTopRightRadius: 27 * u,
        }} />
        <View style={{ position: 'absolute', bottom: 83 * u, left: 148 * u, width: 14 * u, height: 14 * u, borderRadius: 7 * u, backgroundColor: colors.accent500 }} />
        <View style={{ position: 'absolute', bottom: 52 * u, left: 149 * u, width: 12 * u, height: 19 * u, borderRadius: 6 * u, backgroundColor: colors.accent500 }} />
      </View>
    </View>
  );
}
